import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "kubernetes",
  name: "Kubernetes",
  nameEn: "Kubernetes",
  seo: {
    title:
      "Kubernetesを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Kubernetesによるコンテナオーケストレーションを実践的に学べるゆめさくエンジニア。Pod・Service・Deploymentからスケーリングまで、クラウドネイティブ時代の必須スキルを習得しましょう。",
    url: "/columns/kubernetes-programming-school",
  },
  hero: {
    title: "Kubernetesを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Kubernetes",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Kubernetes" },
  ],
  overview: {
    heading: "Kubernetesとは？",
    paragraphs: [
      "Kubernetes（K8s）は、Googleが開発したオープンソースのコンテナオーケストレーションプラットフォームです。Dockerコンテナの自動デプロイ、スケーリング、管理を行い、大規模なアプリケーションの運用を効率化します。",
      "クラウドネイティブ時代において、Kubernetesはインフラの事実上の標準（デファクトスタンダード）となっています。AWS EKS、Google GKE、Azure AKSなど、主要クラウドプロバイダーすべてがKubernetesのマネージドサービスを提供しています。",
      "Pod、Service、Deployment、Ingress、ConfigMapなどのリソースを宣言的に定義し、アプリケーションの可用性・スケーラビリティ・セルフヒーリングを自動的に実現できるのがKubernetesの大きな強みです。",
    ],
    useCases: [
      {
        label: "マイクロサービスの運用基盤",
        description:
          "数十〜数百のマイクロサービスを効率的にデプロイ・管理し、サービス間通信やロードバランシングを自動化します。",
      },
      {
        label: "オートスケーリング",
        description:
          "トラフィックの増減に応じてPod数を自動調整し、コスト最適化とパフォーマンス維持を両立します。",
      },
      {
        label: "CI/CDパイプライン",
        description:
          "GitOps（ArgoCD、Flux）と組み合わせ、コードの変更を自動的にKubernetesクラスタにデプロイする継続的デリバリーを実現します。",
      },
      {
        label: "ML/AIワークロードの実行基盤",
        description:
          "Kubeflowなどを活用し、機械学習モデルの学習・推論パイプラインをKubernetes上で効率的に運用します。",
      },
    ],
  },
  whyLearn: {
    heading: "Kubernetesを学ぶべき3つの理由",
    items: [
      {
        title: "クラウドネイティブのデファクトスタンダード",
        description:
          "Kubernetesはコンテナオーケストレーションの事実上の標準です。AWS、GCP、Azureすべてで採用されており、クラウドインフラに関わるエンジニアにとって避けて通れない技術です。",
      },
      {
        title: "SRE・DevOps職の年収が高い",
        description:
          "Kubernetesスキルを持つSREやDevOpsエンジニアは、IT業界の中でも特に高い年収水準を誇ります。人材不足が深刻で、経験者は引く手あまたの状況が続いています。",
      },
      {
        title: "インフラからアプリまで幅広い知識が身につく",
        description:
          "Kubernetesを学ぶ過程で、ネットワーク、ストレージ、セキュリティ、CI/CDなど、インフラ全般の知識が自然と身につきます。エンジニアとしての総合力が大きく向上します。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでKubernetesを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、上級Phaseのカリキュラムとして Kubernetesを学びます。Dockerの基礎を習得した上で、Kubernetesの概念とリソース管理を段階的に学び、実際のクラスタ構築・運用を実践します。",
      "ローカル環境（Minikube / kind）での学習から始め、クラウド上のマネージドKubernetes（EKS等）へのデプロイまで、実務で必要な一連の流れを体験できます。",
    ],
    topics: [
      "Kubernetesの基本概念（Pod・Service・Deployment）",
      "マニフェストファイル（YAML）の作成と管理",
      "Ingress・ConfigMap・Secretの設定",
      "Horizontal Pod Autoscaler によるオートスケーリング",
      "Helm によるパッケージ管理",
      "GitOps（ArgoCD）による継続的デリバリー",
      "実践プロジェクト：マイクロサービスのKubernetesデプロイ",
    ],
  },
  careerPaths: {
    heading: "Kubernetesを活かせるキャリア",
    items: [
      {
        title: "SRE（Site Reliability Engineer）",
        description:
          "サービスの信頼性と可用性を担保する職種です。Kubernetesクラスタの設計・運用・監視を通じて、大規模サービスの安定稼働を支えます。",
        salary: "700〜1,200万円",
      },
      {
        title: "クラウドアーキテクト",
        description:
          "Kubernetesを中心としたクラウドネイティブアーキテクチャの設計を担当します。マイクロサービス化、コンテナ化戦略、マルチクラウド設計など、技術的な意思決定をリードします。",
        salary: "600〜1,100万円",
      },
      {
        title: "DevOpsエンジニア",
        description:
          "CI/CDパイプラインの構築やインフラの自動化を担当します。Kubernetesと各種ツール（Terraform、ArgoCD等）を組み合わせ、開発チームの生産性を最大化します。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "8〜14ヶ月（前提知識が多く学習範囲が広い）",
        luagate: "4〜5ヶ月（Docker基礎から段階的にステップアップ）",
      },
      {
        label: "挫折率",
        selfStudy: "YAML地獄やネットワーク設定で挫折しやすい",
        luagate: "ハンズオンで一つずつ概念を理解しながら進行",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "ローカルのMiniKubeで止まりがち",
        luagate: "クラウド上の本番相当環境でのデプロイまで経験",
      },
      {
        label: "質問対応",
        selfStudy: "エラーの原因特定にネットワーク知識が必要",
        luagate: "現役SRE・DevOpsメンターが設計から運用まで指導",
      },
      {
        label: "就職サポート",
        selfStudy: "Kubernetes経験をどうアピールすべきかわからない",
        luagate: "クラウドネイティブ企業への就職支援体制",
      },
    ],
  },
  faq: [
    {
      question: "Kubernetesを学ぶ前にDockerの知識は必要ですか？",
      answer:
        "はい、KubernetesはDockerコンテナを管理するツールなので、Dockerの基礎知識は前提となります。ゆめさくエンジニアではDockerの基礎から学べるカリキュラムを用意しているので、コンテナ未経験の方でも安心です。",
    },
    {
      question: "Kubernetesの学習に高スペックなPCは必要ですか？",
      answer:
        "Minikubeやkindを使えば一般的なPCでもローカルにKubernetes環境を構築できます。メモリ8GB以上を推奨しますが、クラウド環境を活用すればPCスペックに依存せず学習を進められます。",
    },
    {
      question: "Kubernetesエンジニアの需要は今後も続きますか？",
      answer:
        "クラウドネイティブ化の流れは加速しており、CNCF（Cloud Native Computing Foundation）の調査でも Kubernetes採用企業は年々増加しています。Kubernetesスキルの需要は中長期的に高い水準が続くと見込まれます。",
    },
    {
      question: "Kubernetesとサーバーレス（AWS Lambda等）のどちらを学ぶべきですか？",
      answer:
        "用途によります。小規模・イベント駆動型ならサーバーレス、大規模・常時稼働型ならKubernetesが適しています。ただし、多くの企業では両方を組み合わせて使っているため、Kubernetesを基盤として理解しておくことをおすすめします。",
    },
  ],
  relatedLanguages: [
    { slug: "docker", name: "Docker" },
    { slug: "aws", name: "AWS" },
    { slug: "terraform", name: "Terraform / IaC" },
    { slug: "linux", name: "Linux" },
  ],
};
